import { css } from "@linaria/core";
import { Modal, type ModalProps } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { GenericIngredientFragment, IngredientFragment } from "../../gql/generated/graphqlSchema.tsx";
import { CreateFromGenericView } from "./CreateFromGenericView.tsx";
import { CreateFromLabelView } from "./CreateFromLabelView.tsx";
import { CreateIngredientChoiceView, type CreateStep } from "./CreateIngredientChoiceView.tsx";
import { CreateManualView } from "./CreateManualView.tsx";

type Step = "choice" | CreateStep;

const genericBody = css`
  height: 660px;
  padding: 0;
`;

const compactBody = css`
  padding: 0;
`;

const widths: Record<Step, number> = {
  choice: 520,
  label: 620,
  generic: 960,
  manual: 520,
};

const bodyClassNames: Record<Step, ModalProps["classNames"]> = {
  choice: undefined,
  label: { body: compactBody },
  generic: { body: genericBody },
  manual: { body: compactBody },
};

export const CreateIngredientModal = ({
  genericIngredient = undefined,
  initialName = "",
  onClose,
  onCreated,
  open,
}: {
  readonly genericIngredient?: GenericIngredientFragment;
  readonly initialName?: string;
  readonly onClose: () => void;
  readonly onCreated: (ingredient: IngredientFragment) => void;
  readonly open: boolean;
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<Step>("choice");
  const skipChoice = genericIngredient != null;
  const step = skipChoice ? "manual" : currentStep;

  const handleCreated = (ingredient: IngredientFragment) => {
    onCreated(ingredient);
    onClose();
  };

  const stepTitles: Record<Step, string> = {
    choice: t("ingredients.createIngredient"),
    label: `${t("ingredients.createIngredient")} › ${t("ingredients.createChoice.fromLabel")}`,
    generic: `${t("ingredients.createIngredient")} › ${t("ingredients.createChoice.fromGeneric")}`,
    manual: `${t("ingredients.createIngredient")} › ${t("ingredients.createChoice.manual")}`,
  };

  return (
    <Modal
      afterClose={() => setCurrentStep("choice")}
      classNames={bodyClassNames[step]}
      destroyOnHidden
      footer={null}
      onCancel={onClose}
      open={open}
      title={skipChoice ? t("ingredients.createIngredient") : stepTitles[step]}
      width={widths[step]}
    >
      {step === "choice" ? <CreateIngredientChoiceView onSelect={setCurrentStep} /> : null}
      {step === "label" ? (
        <CreateFromLabelView onBack={() => setCurrentStep("choice")} onCreated={handleCreated} />
      ) : null}
      {step === "generic" ? (
        <CreateFromGenericView onBack={() => setCurrentStep("choice")} onCreated={handleCreated} />
      ) : null}
      {step === "manual" ? (
        <CreateManualView
          genericIngredient={genericIngredient}
          initialName={initialName}
          onBack={skipChoice ? undefined : () => setCurrentStep("choice")}
          onCreated={handleCreated}
        />
      ) : null}
    </Modal>
  );
};
